/* File upload component using the backend /api/upload endpoint. */
import { useRef, useState } from "react";
import api from "@/lib/api";
import { Upload, X, Loader2, FileText, Eye } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export function buildFileUrl(id) {
  if (!id) return null;
  return `${BACKEND_URL}/api/files/${id}`;
}

async function compressImageIfNeeded(file) {
  if (!(file instanceof File)) return file;
  if (!file.type.startsWith("image/")) return file;
  if (file.type.includes("gif") || file.type.includes("svg")) return file;

  const bitmap = await createImageBitmap(file);
  const maxDimension = 1920;
  const scale = Math.min(
    1,
    maxDimension / Math.max(bitmap.width, bitmap.height),
  );
  const targetW = Math.max(1, Math.round(bitmap.width * scale));
  const targetH = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  bitmap.close();

  const outputType = file.type.includes("png") ? "image/png" : "image/webp";
  const quality = outputType === "image/webp" ? 0.82 : undefined;
  const blob = await new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), outputType, quality);
  });

  if (!blob || blob.size >= file.size) return file;

  const renamed =
    outputType === "image/webp"
      ? file.name.replace(/\.(jpe?g|png|webp)$/i, ".webp")
      : file.name;
  return new File([blob], renamed, {
    type: outputType,
    lastModified: Date.now(),
  });
}

export function FileUploader({
  value,
  onChange,
  accept = ".pdf,.jpg,.jpeg,.png,.webp",
  label,
  tag = "doc",
  testid,
}) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const readyFile = await compressImageIfNeeded(f);
      if (readyFile.size > MAX_UPLOAD_BYTES) {
        toast.error("Max 10MB after compression");
        return;
      }

      const form = new FormData();
      form.append("file", readyFile, readyFile.name);
      const { data } = await api.post(
        `/upload?tag=${encodeURIComponent(tag)}`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      onChange?.(data);
      if (readyFile !== f) {
        toast.success(
          `Image compressed ${(f.size / 1024).toFixed(0)}KB -> ${(readyFile.size / 1024).toFixed(0)}KB`,
        );
      }
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };

  const clear = () => onChange?.(null);

  const fileId = typeof value === "string" ? value : value?.id;
  const isImage =
    typeof value === "object" && !!value?.content_type?.startsWith("image/");
  const previewUrl = buildFileUrl(fileId);
  const fileName =
    typeof value === "object" ? value?.filename : "uploaded-file";
  const fileSizeKb =
    typeof value === "object" ? Math.round((value?.size || 0) / 1024) : null;

  return (
    <div className="w-full" data-testid={testid}>
      {label && (
        <div className="text-[11px] font-semibold text-slate-700 mb-1">
          {label}
        </div>
      )}
      {value ? (
        <div className="relative border border-slate-200 rounded-md p-2 bg-slate-50/50 group hover:bg-white transition">
          <div className="flex items-center gap-2">
            {isImage ? (
              <img
                src={previewUrl}
                alt=""
                className="w-12 h-12 object-cover rounded border border-slate-200"
              />
            ) : (
              <div className="w-12 h-12 rounded bg-rose-50 border border-rose-200 flex items-center justify-center">
                <FileText className="w-5 h-5 text-rose-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-slate-800 truncate">
                {fileName || "file"}
              </div>
              <div className="text-[10px] text-slate-500">
                {fileSizeKb != null ? `${fileSizeKb} KB` : "Uploaded"}
              </div>
            </div>
            <a
              href={previewUrl}
              target="_blank"
              rel="noreferrer"
              className="p-1 rounded hover:bg-slate-100 text-slate-600"
              data-testid={`${testid}-preview`}
            >
              <Eye className="w-3.5 h-3.5" />
            </a>
            <button
              onClick={clear}
              className="p-1 rounded hover:bg-red-50 text-red-600"
              data-testid={`${testid}-clear`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => ref.current?.click()}
          disabled={busy}
          data-testid={`${testid}-upload`}
          className="w-full border-2 border-dashed border-slate-300 rounded-md p-3 hover:border-[var(--erp-primary)] hover:bg-slate-50 transition text-center"
        >
          {busy ? (
            <div className="flex items-center justify-center gap-2 text-slate-500 text-xs">
              <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 text-slate-500 text-xs">
              <Upload className="w-4 h-4" />
              <span>Click to upload</span>
              <span className="text-slate-400">· PDF/JPG/PNG · max 10MB</span>
            </div>
          )}
        </button>
      )}
      <input ref={ref} type="file" accept={accept} hidden onChange={upload} />
    </div>
  );
}
